import { onMount, onDestroy } from '@/modules/_'
import { Draggable } from '@/lib/gsap'

/**
 * Draggable Module
 * 
 * Creates draggable elements within specified containers using GSAP Draggable.
 * Elements stay within container bounds and support z-index management.
 * ----------------
 * Usage in Webflow:
 * 1. Create a container div with data-target="draggable-container"
 * 2. Add child elements with data-target="draggable-element"
 * 3. Add data-module="draggable" to container or parent element
 * 
 * Features:
 * - Automatically disabled on touch devices
 * - Respects prefers-reduced-motion
 * - Disabled on tablet and smaller screens (max-width: 991px)
 * - Elements can be clicked to bring to front (z-index boost)
 * - Bounds checking keeps elements inside container
 * ----------------
 */

export default function (element, dataset) {
  // Store draggable instances for cleanup
  let draggableInstances = []

  // Check if we should enable dragging
  const shouldDisable = () => {
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const isTabletOrSmaller = window.matchMedia('(max-width: 991px)').matches

    return isTouchDevice || prefersReducedMotion || isTabletOrSmaller
  }

  const initDraggable = () => {
    // Don't initialize if conditions aren't right
    if (shouldDisable()) {
      return
    }

    // Find all containers - either from element itself or children
    let containers
    if (element.hasAttribute('data-target') && element.dataset.target === 'draggable-container') {
      containers = [element]
    } else {
      containers = Array.from(element.querySelectorAll('[data-target="draggable-container"]'))
    }

    if (!containers.length) {
      console.warn('No draggable containers found')
      return
    }

    // Initialize each container
    containers.forEach((container, containerIndex) => {
      const elements = Array.from(
        container.querySelectorAll('[data-target="draggable-element"]')
      )

      if (!elements.length) {
        console.warn(`No draggable elements found in container ${containerIndex}`)
        return
      }

      // Calculate initial z-index counter from existing elements
      let zCounter = elements.reduce(
        (max, el) => Math.max(max, parseInt(getComputedStyle(el).zIndex, 10) || 0),
        0
      )

      // Create draggable instances
      const instances = Draggable.create(elements, {
        type: 'x,y',
        bounds: container,
        inertia: false,
        zIndexBoost: false,
        onPress: function () {
          this.target.style.zIndex = String(++zCounter)
          this.target.classList.add('is-dragging')
        },
        onRelease: function () {
          this.target.classList.remove('is-dragging')
        }
      })

      // Store instances for cleanup
      draggableInstances.push(...instances)
    })
  }

  // Lifecycle: Mount
  onMount(() => {
    initDraggable()
  })

  // Lifecycle: Cleanup
  onDestroy(() => {

    // Kill all draggable instances
    draggableInstances.forEach((instance) => {
      if (instance && instance.kill) {
        instance.kill()
      }
    })

    // Clear array
    draggableInstances = []
  })
}

